import { ACTIONS, Action, Automation, Condition, FlowCall, FlowElement } from 'prace-common-components'
import { TemplateListItem } from 'store/api/templates/types'

/* The condition is returned with the element it is attached to, so the UI can link back to it */
export type UsedCondition = Condition & { ownerId: FlowElement['id'] }

type FlowNode = { id: FlowElement['id']; flow?: FlowNode[] }

/**
 * Every flow element id inside an element, including its own: the forms and elements of a step,
 * the elements of a form, the children of a group. Deleting an element deletes all of these, so
 * they are all part of the same deletion set.
 */
export const collectElementIds = (node: FlowNode): FlowElement['id'][] =>
  [node.id, ...(node.flow || []).flatMap(collectElementIds)]

/**
 * This function is used to get the usage of one or more flow elements in automations, conditions,
 * and templates.
 *
 * Dependencies between the elements being deleted are not usages: if A and B are deleted together,
 * B's condition on A does not block the deletion. Pass every id going away in the same call
 * (see collectElementIds) so those are filtered out.
 *
 * @param {number | number[]} flowElementIds - The ID(s) of the flow element(s) being deleted.
 * @param {FlowCall} call - The flow call object.
 * @param {TemplateListItem[]} templates - The array of templates.
 *
 * @returns {Object} - An object containing arrays of automations, conditions, and templates that
 * use the flow element(s) and are not themselves being deleted.
 */
export const getElementUsage = (
  flowElementIds: FlowElement['id'] | FlowElement['id'][],
  call: FlowCall,
  templates: TemplateListItem[],
) => {
  const deleted = new Set(Array.isArray(flowElementIds) ? flowElementIds : [flowElementIds])
  const isDeleted = (id?: Nullable<number>) => id != null && deleted.has(id)

  // Initialize arrays to store automations and conditions that target the flow elements
  let automationsTargetting: Automation[] = []
  let conditionsTargetting: UsedCondition[] = []

  const usesDeleted = (condition: Condition) =>
    !!condition.validation?.elementsIds?.some(isDeleted) ||
      !!condition.validation?.elements?.some((element) => isDeleted(element.id))

  /* Conditions attached to an element that is also going away are not blocking */
  const collectConditions = (owner: { id: number; effects?: Condition[] }) => {
    if(isDeleted(owner.id)) return
    conditionsTargetting = conditionsTargetting.concat(
      (owner.effects || []).filter(usesDeleted).map((condition) => ({ ...condition, ownerId: owner.id })),
    )
  }

  // Iterate over each step in the flow
  call?.flow?.forEach((step) => {
    /* A step being deleted takes its own automations with it */
    if(isDeleted(step.id)) return

    // Check if there are automations using these elements
    const automations = (step.automations || []).filter(
      (automation) => isDeleted((automation?.condition?.conditionalElement as { id: number })?.id) ||
        automation?.targets?.some((target) => isDeleted(target.targetElement?.id)) ||
        isDeleted((automation?.context as Action<ACTIONS.VISIBILITY>['context'])?.flowElement?.id),
    ) || []
    // Add the found automations to the array
    automationsTargetting = automationsTargetting.concat(automations)

    // Iterate over each form in the step
    step.flow.forEach((form) => {
      if(isDeleted(form.id)) return
      // Check if there are form visibility conditions using these elements
      collectConditions(form)

      form.flow.forEach((element) => {
        // Check if there are conditions using these elements
        collectConditions(element)
        // If the element has a flow, iterate over each group element in the flow
        if(element.flow?.length) element.flow.forEach(collectConditions)
      })
    })
  })

  /* The listings return neither the contents nor the variables of a template, so every flow element
     it points at (variables, and the elements used by condition and group nodes) comes from elementIds. */
  const templatesTargetting = (templates || []).filter((template: TemplateListItem) =>
    template.elementIds?.some(isDeleted))

  // Return the arrays of automations, conditions, and templates that use the flow element(s)
  return {
    automations: automationsTargetting,
    conditions: conditionsTargetting,
    templates: templatesTargetting,
  }
}
